export type SubscriptionConnectionState =
  | "closed"
  | "connecting"
  | "opened"
  | "connected"
  | "error";

export type SubscriptionHealth =
  | "closed"
  | "connecting"
  | "connected"
  | "stale"
  | "error";

type DiagnosticValue = boolean | number | string | null | readonly string[];
type DiagnosticData = Record<string, DiagnosticValue>;

export type SubscriptionDiagnosticEvent = {
  kind:
    | "connection"
    | "connection_error"
    | "subscription_started"
    | "subscription_received"
    | "subscription_stopped"
    | "subscription_error";
  at: string;
  data: DiagnosticData;
};

export type SubscriptionReceiptSummary = {
  at: string;
  operationName: string;
  fields: readonly string[];
  hasErrors: boolean;
};

export type SubscriptionCloseSummary = {
  at: string;
  code: number | null;
  reason: string | null;
};

export type SubscriptionDiagnosticsSnapshot = {
  health: SubscriptionHealth;
  connectionState: SubscriptionConnectionState;
  connectionAttempts: number;
  reconnectCount: number;
  connectedAt: string | null;
  lastHeartbeatAt: string | null;
  lastPongAt: string | null;
  lastReceivedAt: string | null;
  lastError: string | null;
  lastClose: SubscriptionCloseSummary | null;
  activeSubscriptions: readonly string[];
  receivedCounts: Readonly<Record<string, number>>;
  protocolMessageCounts: Readonly<Record<string, number>>;
  lastReceived: SubscriptionReceiptSummary | null;
  recentEvents: readonly SubscriptionDiagnosticEvent[];
};

export type SubscriptionDiagnosticsOptions = {
  maxEvents?: number;
  heartbeatStaleAfterMs?: number;
  now?: () => number;
  onEvent?: (event: SubscriptionDiagnosticEvent) => void;
  onSnapshot?: (snapshot: SubscriptionDiagnosticsSnapshot) => void;
};

const DEFAULT_MAX_EVENTS = 50;
const DEFAULT_HEARTBEAT_STALE_AFTER_MS = 40_000;
const MAX_DIAGNOSTIC_TEXT_LENGTH = 200;
const MAX_FIELDS_PER_RECEIPT = 10;

const sanitizeText = (value: string) =>
  value
    .replace(
      /(["'])(session[_-]?key|authorization|set-cookie|token|cookie)\1(\s*[:=]\s*)(["'])(?:\\.|[^"'])*\4/gi,
      "$1$2$1$3$4[REDACTED]$4"
    )
    .replace(
      /(authorization)(\s*[:=]\s*)(?!\[REDACTED\])(?:"[^"]*"|'[^']*'|[^\r\n}]*?(?=\s+\{|[\r\n}]|$))/gi,
      "$1$2[REDACTED]"
    )
    .replace(
      /((?:set-)?cookie)(\s*[:=]\s*)(?!\[REDACTED\])(?:"[^"]*"|'[^']*'|[^\r\n}]*?(?=\s+\{|[\r\n}]|$))/gi,
      "$1$2[REDACTED]"
    )
    .replace(
      /(session[_-]?key|authorization|set-cookie|token|cookie)(\s*[:=]\s*)(?:bearer\s+)?["']?(?!\[REDACTED\])[^,\s"'}]+["']?/gi,
      "$1$2[REDACTED]"
    )
    .slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH);

const hasMessage = (value: unknown): value is { message: string } =>
  value != null &&
  typeof value === "object" &&
  "message" in value &&
  typeof value.message === "string";

const errorMessage = (error: unknown) => {
  if (Array.isArray(error)) {
    const messages = error
      .filter(hasMessage)
      .map(({ message }) => message)
      .filter((message) => message.length > 0);

    return messages.length > 0
      ? sanitizeText(messages.join("; "))
      : "Unknown subscription error";
  }

  if (error instanceof Error) {
    return sanitizeText(error.message);
  }

  if (typeof error === "string") {
    return sanitizeText(error);
  }

  if (hasMessage(error)) {
    return sanitizeText(error.message);
  }

  return "Unknown subscription error";
};

const validGraphQLField = (field: string) =>
  /^[_A-Za-z][_0-9A-Za-z]*$/.test(field);

const subscriptionName = (name: string) => {
  const normalizedName = name.trim();
  return validGraphQLField(normalizedName)
    ? normalizedName.slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH)
    : "anonymous";
};

const sanitizeFields = (fields: readonly string[]) =>
  Array.from(new Set(fields.filter(validGraphQLField))).slice(
    0,
    MAX_FIELDS_PER_RECEIPT
  );

const timestamp = (value: number) => new Date(value).toISOString();

const increment = (counts: Record<string, number>, key: string) => {
  counts[key] = (counts[key] ?? 0) + 1;
};

export class SubscriptionDiagnostics {
  private readonly maxEvents: number;
  private readonly heartbeatStaleAfterMs: number;
  private readonly now: () => number;
  private readonly onEvent?: (event: SubscriptionDiagnosticEvent) => void;
  private readonly onSnapshot?: (
    snapshot: SubscriptionDiagnosticsSnapshot
  ) => void;
  private readonly recentEvents: SubscriptionDiagnosticEvent[] = [];
  private readonly activeSubscriptionCounts: Record<string, number> = {};
  private readonly receivedCounts: Record<string, number> = {};
  private readonly protocolMessageCounts: Record<string, number> = {};
  private connectionState: SubscriptionConnectionState = "closed";
  private connectionAttempts = 0;
  private reconnectCount = 0;
  private connectedAtMs: number | null = null;
  private lastHeartbeatAtMs: number | null = null;
  private lastPongAtMs: number | null = null;
  private lastReceivedAtMs: number | null = null;
  private lastError: string | null = null;
  private lastClose: SubscriptionCloseSummary | null = null;
  private lastReceived: SubscriptionReceiptSummary | null = null;

  constructor(options: SubscriptionDiagnosticsOptions = {}) {
    this.maxEvents = Math.max(1, options.maxEvents ?? DEFAULT_MAX_EVENTS);
    this.heartbeatStaleAfterMs = Math.max(
      1,
      options.heartbeatStaleAfterMs ?? DEFAULT_HEARTBEAT_STALE_AFTER_MS
    );
    this.now = options.now ?? Date.now;
    this.onEvent = options.onEvent;
    this.onSnapshot = options.onSnapshot;
  }

  recordConnecting(isRetry: boolean) {
    this.connectionState = "connecting";
    this.connectionAttempts += 1;

    if (isRetry) {
      this.reconnectCount += 1;
    }

    this.recordEvent("connection", {
      state: "connecting",
      retry: isRetry,
    });
  }

  recordOpened() {
    this.connectionState = "opened";
    this.recordEvent("connection", { state: "opened" });
  }

  recordConnected(isRetry: boolean) {
    const now = this.now();
    this.connectionState = "connected";
    this.connectedAtMs = now;
    this.lastHeartbeatAtMs = now;

    this.recordEvent("connection", {
      state: "connected",
      retry: isRetry,
    });
  }

  recordClosed(code: number | null, reason: unknown) {
    const now = this.now();
    const sanitizedReason = reason == null ? null : errorMessage(reason);
    this.connectionState = "closed";
    this.lastClose = {
      at: timestamp(now),
      code,
      reason: sanitizedReason,
    };

    this.recordEvent("connection", {
      state: "closed",
      code,
      reason: sanitizedReason,
    });
  }

  recordError(error: unknown) {
    this.connectionState = "error";
    this.lastError = errorMessage(error);
    this.recordEvent("connection_error", { message: this.lastError });
  }

  recordPing(received: boolean) {
    increment(this.protocolMessageCounts, "ping");

    if (received) {
      this.lastHeartbeatAtMs = this.now();
    }

    this.emitSnapshot();
  }

  recordPong(received: boolean) {
    increment(this.protocolMessageCounts, "pong");

    if (received) {
      const now = this.now();
      this.lastHeartbeatAtMs = now;
      this.lastPongAtMs = now;
    }

    this.emitSnapshot();
  }

  recordProtocolMessage(messageType: string) {
    if (messageType === "ping" || messageType === "pong") {
      return;
    }

    increment(this.protocolMessageCounts, messageType);
    this.emitSnapshot();
  }

  recordSubscriptionStarted(operationName: string) {
    const name = subscriptionName(operationName);
    this.activeSubscriptionCounts[name] =
      (this.activeSubscriptionCounts[name] ?? 0) + 1;
    this.recordEvent("subscription_started", { operationName: name });
  }

  recordSubscriptionReceived(
    operationName: string,
    fields: readonly string[],
    hasErrors: boolean
  ) {
    const now = this.now();
    const name = subscriptionName(operationName);
    const sanitizedFields = sanitizeFields(fields);
    const receivedKey = sanitizedFields[0] ?? name;
    const receipt = {
      at: timestamp(now),
      operationName: name,
      fields: sanitizedFields,
      hasErrors,
    } satisfies SubscriptionReceiptSummary;

    this.lastReceivedAtMs = now;
    this.lastReceived = receipt;
    increment(this.receivedCounts, receivedKey);
    this.recordEvent("subscription_received", {
      operationName: name,
      fields: sanitizedFields,
      hasErrors,
    });
  }

  recordSubscriptionStopped(operationName: string) {
    const name = subscriptionName(operationName);
    const count = this.activeSubscriptionCounts[name] ?? 0;

    if (count <= 1) {
      delete this.activeSubscriptionCounts[name];
    } else {
      this.activeSubscriptionCounts[name] = count - 1;
    }

    this.recordEvent("subscription_stopped", { operationName: name });
  }

  recordSubscriptionError(operationName: string, error: unknown) {
    const message = errorMessage(error);
    this.lastError = message;
    this.recordEvent("subscription_error", {
      operationName: subscriptionName(operationName),
      message,
    });
  }

  snapshot(): SubscriptionDiagnosticsSnapshot {
    return {
      health: this.health(),
      connectionState: this.connectionState,
      connectionAttempts: this.connectionAttempts,
      reconnectCount: this.reconnectCount,
      connectedAt:
        this.connectedAtMs == null ? null : timestamp(this.connectedAtMs),
      lastHeartbeatAt:
        this.lastHeartbeatAtMs == null
          ? null
          : timestamp(this.lastHeartbeatAtMs),
      lastPongAt:
        this.lastPongAtMs == null ? null : timestamp(this.lastPongAtMs),
      lastReceivedAt:
        this.lastReceivedAtMs == null ? null : timestamp(this.lastReceivedAtMs),
      lastError: this.lastError,
      lastClose: this.lastClose,
      activeSubscriptions: Object.keys(this.activeSubscriptionCounts).sort(),
      receivedCounts: { ...this.receivedCounts },
      protocolMessageCounts: { ...this.protocolMessageCounts },
      lastReceived: this.lastReceived,
      recentEvents: [...this.recentEvents],
    };
  }

  private health(): SubscriptionHealth {
    if (this.connectionState === "connected") {
      const heartbeatAt = this.lastHeartbeatAtMs ?? this.connectedAtMs;

      if (
        heartbeatAt != null &&
        this.now() - heartbeatAt > this.heartbeatStaleAfterMs
      ) {
        return "stale";
      }

      return "connected";
    }

    if (this.connectionState === "error") {
      return "error";
    }

    if (
      this.connectionState === "connecting" ||
      this.connectionState === "opened"
    ) {
      return "connecting";
    }

    return "closed";
  }

  private recordEvent(
    kind: SubscriptionDiagnosticEvent["kind"],
    data: DiagnosticData
  ) {
    const event = {
      kind,
      at: timestamp(this.now()),
      data,
    } satisfies SubscriptionDiagnosticEvent;

    this.recentEvents.push(event);

    if (this.recentEvents.length > this.maxEvents) {
      this.recentEvents.splice(0, this.recentEvents.length - this.maxEvents);
    }

    this.onEvent?.(event);
    this.emitSnapshot();
  }

  private emitSnapshot() {
    this.onSnapshot?.(this.snapshot());
  }
}

type SubscriptionDiagnosticsClientEntry = {
  id: string;
  diagnostics: SubscriptionDiagnostics;
};

export type SubscriptionDiagnosticsContext = {
  clientCount: number;
  clients: Readonly<Record<string, SubscriptionDiagnosticsSnapshot>>;
};

const subscriptionDiagnosticsByClient = new Map<
  object,
  SubscriptionDiagnosticsClientEntry
>();
let nextSubscriptionDiagnosticsClientId = 1;

export const setSubscriptionDiagnosticsForClient = (
  client: object,
  diagnostics: SubscriptionDiagnostics
) => {
  const existingEntry = subscriptionDiagnosticsByClient.get(client);
  const id =
    existingEntry?.id ?? `client_${nextSubscriptionDiagnosticsClientId++}`;

  subscriptionDiagnosticsByClient.set(client, { id, diagnostics });
};

export const clearSubscriptionDiagnosticsForClient = (client: object) => {
  subscriptionDiagnosticsByClient.delete(client);
};

export const getSubscriptionDiagnosticsForClient = (client: object) =>
  subscriptionDiagnosticsByClient.get(client)?.diagnostics ?? null;

export const getSubscriptionDiagnosticsContext =
  (): SubscriptionDiagnosticsContext => ({
    clientCount: subscriptionDiagnosticsByClient.size,
    clients: Object.fromEntries(
      Array.from(subscriptionDiagnosticsByClient.values()).map(
        ({ id, diagnostics }) => [id, diagnostics.snapshot()]
      )
    ),
  });
